import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classnames from "classnames";

import IconClose from "assets/svg/close.svg";

import styles from "./styles.module.css";

const Modal = ({
  isOpen = true,
  title,
  children,
  onClose,
  size = "medium",
  customHeader,
  customFooter,
  className,
  overlayClassName,
  zIndex,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
      previousActiveElement.current?.focus();
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const containerClasses = classnames(
    styles.modalContainer,
    styles[`modal-${size}`],
    {
      [styles.open]: isVisible,
    },
    className
  );

  const headerClass = classnames({
    [styles.modalHeader]: true,
    [styles.modalHeaderWithoutTittle]: title,
  });

  const overlayClasses = classnames(styles.modalOverlay, overlayClassName);

  return createPortal(
    <div style={zIndex ? { zIndex } : {}}>
      <div className={overlayClasses} onClick={handleClose} aria-hidden="true" />
      <div
        ref={modalRef}
        className={containerClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Modal içeriği */}
        <div className={headerClass}>
          <div className={styles.modalHeaderInner}>
            {!customHeader ? (
              <>
                <h2 id="modal-title" className={styles.headerTitle}>
                  {title}
                </h2>
                <button className={styles.closeButton} onClick={handleClose} aria-label="Close modal" type="button">
                  <IconClose />
                </button>
              </>
            ) : (
              customHeader
            )}
          </div>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.modalContentInner}>{children}</div>
        </div>
        {customFooter && <div className={styles.modalFooter}>{customFooter}</div>}
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  customHeader: PropTypes.node,
  customFooter: PropTypes.node,
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
  zIndex: PropTypes.number,
};

export default Modal;
